import { DefaultListEventsPayload, ListEventsPayload } from '@src/entities/Event/includes/types';
import { Button } from '@src/shared/ui/components/Button';
import { DateRange } from '@src/shared/ui/components/DateRange';
import { ErrorHandler } from '@src/shared/ui/components/ErrorHandler';
import { useForm, useFormReturn } from '@uhg-abyss/web/hooks/useForm';
import { dayjs } from '@uhg-abyss/web/tools/dayjs';
import { FormProvider } from '@uhg-abyss/web/ui/FormProvider';
import { Layout } from '@uhg-abyss/web/ui/Layout';
import React, { useCallback } from 'react';

import { Styles } from './includes/styles';

type DateRangeValue = {
  end: string;
  start: string;
};

type FiltersFormValues = {
  dateRange: DateRangeValue;
};

type FiltersProps = {
  requestArgs: ListEventsPayload;
  setRequestArgs?: (args: ListEventsPayload) => void;
};

/**
 * Filters
 *
 * Displays a form to filter the data displayed in the table by date range.
 * Submits the selected date range to update the event list.
 */
export function Filters({ requestArgs = DefaultListEventsPayload, setRequestArgs = () => {} }: FiltersProps) {
  const defaultValues: FiltersFormValues = {
    dateRange: {
      end: dayjs(new Date()).add(6, 'months').format('MM/DD/YYYY'),
      start: dayjs(new Date()).subtract(6, 'months').format('MM/DD/YYYY'),
    },
  };

  const form: useFormReturn<FiltersFormValues> = useForm({ defaultValues });
  const { isSubmitting, isValid } = form.formState;

  /**
   * Handles form submission to update the event list based on the selected date range.
   */
  const handleSubmit = useCallback(
    (submittedValues: FiltersFormValues): void => {
      if (!isSubmitting && isValid) {
        setRequestArgs({
          ...requestArgs,
          endDate: dayjs(submittedValues?.dateRange?.end).format('YYYY-MM-DD'),
          startDate: dayjs(submittedValues?.dateRange?.start).format('YYYY-MM-DD'),
        });
      }
    },
    [isSubmitting, isValid, requestArgs, setRequestArgs]
  );

  return (
    <ErrorHandler location="src/entities/Event/ui/components/tables/Events/components/Filters/Filters.tsx">
      <Styles>
        <FormProvider autoComplete="off" highlighted onSubmit={handleSubmit} state={form}>
          <Layout.Group alignItems="bottom" alignLayout="left">
            <div>
              <DateRange
                defaultEndDate={form.getValues('dateRange.end')}
                defaultStartDate={form.getValues('dateRange.start')}
                isClearable={false}
                isRequired={false}
                onChange={(value: { endDate: string; startDate: string }) => {
                  form.setValue('dateRange.start', value.startDate, {
                    shouldDirty: true,
                    shouldValidate: true,
                  });

                  form.setValue('dateRange.end', value?.endDate, {
                    shouldDirty: true,
                    shouldValidate: true,
                  });

                  form.validate(
                    'dateRange.start',
                    () => {},
                    () => {}
                  );
                  form.validate(
                    'dateRange.end',
                    () => {},
                    () => {}
                  );
                }}
              />
            </div>
            <div>
              <Button type="submit" variant="solid">
                Apply
              </Button>
            </div>
          </Layout.Group>
        </FormProvider>
      </Styles>
    </ErrorHandler>
  );
}
